import React, { useState, useEffect, useRef } from "react";
import LeadContent from "./../components/leads/LeadContent.tsx";
import LeadsList from "./../components/leads/LeadsList.tsx";
import Header from "../components/Header.tsx";
import SettingsPopUp from "../components/SettingsPopUp.tsx";
import NotificationToast from "../components/NotificationToast.tsx";
import api from "../services/api.ts";
import { Lead } from "../types/leads.ts";
// import { LEADS_DATA } from "../data/mocked-data";

const LeadsPage = ({
  user,
  profileSettingsIsOpen,
  setProfileSettingsIsOpen,
}) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [leads, setLeads] = useState([]);
  const [isMobileView, setIsMobileView] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [activeNotification, setActiveNotification] = useState<any | null>(
    null
  );
  const previousNotificationIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const apiLeads: Lead[] = await api.getLeads();
        if (!mounted) return;
        const uiLeads = (apiLeads || []).map((l) => {
          const firstName = (l.firstName ?? "").trim();
          const lastName = (l.lastName ?? "").trim();
          const name =
            firstName || lastName
              ? `${firstName}${firstName && lastName ? " " : ""}${lastName}`
              : l.name ?? l.email ?? "Unknown";

          const ts = l.lastActivity ?? l.lastContacted ?? l.createdAt ?? null;
          const messageTime = ts ? new Date(ts).toLocaleString() : "";

          // Map intel -> UI badges
          const intelArr = Array.isArray((l as any).intel)
            ? (l as any).intel
            : [];
          const buySellRaw =
            intelArr.find((i: any) => i.type === "BUY_SELL")?.value || "";
          const roleBadge =
            buySellRaw === "B_AND_S"
              ? "B & S"
              : buySellRaw === "SELLER"
              ? "Seller"
              : buySellRaw === "BUYER"
              ? "Buyer"
              : "Lead";

          return {
            id: String(l.id ?? l.fubId ?? l.externalId ?? crypto.randomUUID()),
            name,
            email: l.email ?? "",
            phone: l.phoneNumber ?? l.phone ?? "",
            messageTime,
            message: l.notes ?? "",
            missedMassages: l.unreadCount ?? 0,
            answerType: l.answerType ?? "Suggested",
            role: roleBadge,
            type: "FUB",
            rating:
              (Array.isArray((l as any).intel)
                ? Number(
                    intelArr.find((i: any) => i.type === "QUALITY_SCORE")
                      ?.value || 0
                  )
                : l.score ?? 0) || 0,
            notification: Boolean(l.notification),
            parsedNotificTime: l.parsedNotificTime ?? null,

            // extra fields exposed to UI (for cards/details/filter)
            stage: l.stage ?? "Lead",
            tags: Array.isArray(l.tags) ? l.tags : [],
            source: l.source ?? undefined,
            status: l.status ?? undefined,
            city: l.city ?? null,
            state: l.state ?? null,
            country: l.country ?? null,
            zipCode: l.zipCode ?? null,
            address: l.address ?? null,
            assignedTo: l.assignedTo ?? undefined,
            assignedToId: (l as any).assignedToId ?? null,
            assignedToEmail: (l as any).assignedToEmail ?? null,
            budget: l.budget ?? null,
            preApproved: l.preApproved ?? false,
            lastActivity: l.lastActivity ?? null,
            lastContacted: l.lastContacted ?? null,
            createdAt: l.createdAt,
            updatedAt: l.updatedAt,

            // keep full payload for detail components
            rawLead: l,
          };
        });
        setLeads(uiLeads);
        if (uiLeads.length) setActiveId(String(uiLeads[0].id));
      } catch (e) {
        console.error("Failed to load leads", e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 1024);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleChangeLeadData = (id, field, value) => {
    setLeads((prevLeads) =>
      prevLeads.map((lead) =>
        String(lead.id) === String(id) ? { ...lead, [field]: value } : lead
      )
    );
  };

  const handleDeleteLead = (id) => {
    setLeads((prevLeads) => {
      const newLeads = prevLeads.filter(
        (lead) => String(lead.id) !== String(id)
      );
      if (String(activeId) === String(id) && newLeads.length > 0) {
        setActiveId(String(newLeads[0].id));
      }
      return newLeads;
    });
  };

  const handleChatSelect = (id) => {
    setActiveId(String(id));
    if (isMobileView) {
      setIsChatOpen(true);
    }
  };

  const handleBackToList = () => {
    setIsChatOpen(false);
  };

  // Notification handling
  const handleNavigateToLead = (leadId: string) => {
    setActiveId(leadId);
    if (isMobileView) {
      setIsChatOpen(true);
    }
  };

  const handleDismissNotification = (notificationId: string) => {
    setActiveNotification(null);
  };

  // Poll for new notifications
  useEffect(() => {
    const pollNotifications = async () => {
      try {
        console.log("🔔 [LeadsPage] Fetching notifications...");
        const newNotifications = await api.getNotifications(5);
        console.log("📊 [LeadsPage] Notifications data:", newNotifications);

        // Get current notification IDs
        const currentIds = new Set(newNotifications.map((n) => n.id));
        const previousIds = previousNotificationIdsRef.current;

        // Find new notifications (not in previous set)
        const newNotificationIds = newNotifications.filter(
          (n) => !previousIds.has(n.id)
        );

        // Update previous IDs reference
        previousNotificationIdsRef.current = currentIds;

        // Show toast only for new unread notifications
        setActiveNotification((prevActiveNotification) => {
          if (newNotificationIds.length > 0 && !prevActiveNotification) {
            const newUnreadNotifications = newNotificationIds.filter(
              (n) => !n.isRead
            );
            console.log(
              "🔔 [LeadsPage] New unread notifications:",
              newUnreadNotifications
            );
            if (newUnreadNotifications.length > 0) {
              const latestNotification = newUnreadNotifications[0];
              console.log(
                "🔔 [LeadsPage] Setting active notification:",
                latestNotification
              );
              return latestNotification;
            }
          }
          return prevActiveNotification;
        });
      } catch (error) {
        console.error("❌ [LeadsPage] Failed to fetch notifications:", error);
      }
    };

    // Initial fetch
    pollNotifications();

    // Poll every 5 minutes (5 * 60 * 1000 = 300000ms)
    const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes = 300000ms
    console.log(
      `🔔 [LeadsPage] Setting notification poll interval to ${POLL_INTERVAL_MS}ms (${
        POLL_INTERVAL_MS / 1000 / 60
      } minutes)`
    );
    const interval = setInterval(pollNotifications, POLL_INTERVAL_MS);
    return () => {
      console.log("🔔 [LeadsPage] Clearing notification poll interval");
      clearInterval(interval);
    };
  }, []);

  return (
    <>
      <Header
        setProfileSettingsIsOpen={setProfileSettingsIsOpen}
        onNavigateToLead={handleNavigateToLead}
      />
      <div className="flex gap-x-5 px-5 pt-3 pb-5 overflow-hidden">
        <div
          className={`${
            isMobileView && isChatOpen ? "hidden" : "flex-1 w-full"
          }`}
        >
          <LeadsList
            activeId={activeId}
            setActiveId={handleChatSelect}
            leads={leads}
            handleChangeLeadData={handleChangeLeadData}
          />
        </div>
        {(!isMobileView || (isMobileView && isChatOpen)) && (
          <div
            className={`${
              isMobileView ? "fixed inset-0 z-50 bg-white" : "flex-2 w-full"
            }`}
          >
            <LeadContent
              activeId={activeId}
              leads={leads}
              handleChangeLeadData={handleChangeLeadData}
              handleDeleteLead={handleDeleteLead}
              onBack={isMobileView ? handleBackToList : undefined}
              isMobileView={isMobileView}
            />
          </div>
        )}
      </div>
      {profileSettingsIsOpen && (
        <SettingsPopUp
          setProfileSettingsIsOpen={setProfileSettingsIsOpen}
          user={user}
        />
      )}
      {activeNotification && (
        <NotificationToast
          notification={activeNotification}
          onDismiss={handleDismissNotification}
          onViewLead={handleNavigateToLead}
        />
      )}
    </>
  );
};

export default LeadsPage;
